import { Box, Divider, Stack, Typography } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import { Done } from "lib/@components/done"
import { If } from "lib/@components/switch"
import { frag } from "lib/@components/slot/frag"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { Comparison } from "routes/schedule/compare/lib/diff3"
import { AmendsBoundary } from "./amends-boundary"
import { AmendsBoundaryEditor } from "./amends-boundary-editor"
import { AmendsEditAction } from "./audience-aware-amends-editor"
import { createDraftAmendHandlers } from "../controller/draft-amend-handlers"

export function IntroValueDialog({ originalIntro, tailoredIntro, entry, projectId, scheduleId, editMode = true }) {
    const hasOriginal = hasVisibleHtmlContent(originalIntro)
    const hasNext = hasVisibleHtmlContent(tailoredIntro)
    const amendsKey = entry?.key ?? null
    const noteAmends = getNoteAmends(entry, amendsKey)
    const { saveAmend, deleteAmend } = createDraftAmendHandlers({ projectId, scheduleId })
    // Introduction/Notes are read only from the author notes tab; remove the plugged Edit button
    AmendsEditAction.usePlug(editMode ? null : replaceAllWith(frag), [editMode])

    return (
        <DialogBox title="Schedule Introduction/Notes Comparison" Actions={Done}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: "stretch" }}>
                    <Stack spacing={0.75} sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">New</Typography>
                        {buildIntroContent({
                            hasContent: hasNext,
                            dataCy: "intro-tailored-dialog-new",
                            original: originalIntro,
                            other: originalIntro,
                            text: tailoredIntro,
                        })}
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Stack spacing={0.75} sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">Original</Typography>
                        {buildIntroContent({
                            hasContent: hasOriginal,
                            dataCy: "intro-tailored-dialog-original",
                            original: tailoredIntro,
                            other: tailoredIntro,
                            text: originalIntro,
                            showRemoved: true,
                        })}
                    </Stack>
                </Stack>
                <If truthy={Boolean(amendsKey)}>
                    <Divider />
                    <Typography variant="subtitle2">Author Notes</Typography>
                    <AmendsBoundary
                        amends={normalizeAmendsForBoundary(noteAmends, amendsKey)}
                        onSaveAmend={saveAmend}
                        onDeleteAmend={deleteAmend}
                        options={{ editMode }}
                    >
                        <AmendsBoundaryEditor amendsKey={amendsKey} alwaysOpen />
                    </AmendsBoundary>
                </If>
            </Stack>
        </DialogBox>
    )
}

function buildIntroContent({ hasContent, dataCy, original, other, text, showRemoved = false }) {
    if (!hasContent) {
        return (
            <Box sx={{ p: 1.25, borderRadius: 1, border: "1px solid", borderColor: "divider" }} data-cy={dataCy}>
                <Typography variant="body2" color="text.secondary">
                    (empty)
                </Typography>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                wordBreak: "break-word",
            }}
            data-cy={dataCy}
        >
            <Comparison original={original} other={other} text={text} showRemoved={showRemoved} normalizeHtml />
        </Box>
    )
}

function hasVisibleHtmlContent(value) {
    return `${value ?? ""}`.replace(/<[^>]*>/g, "").trim().length > 0
}

function getNoteAmends(entry, amendsKey) {
    if (!amendsKey || !entry?.amends) return []
    return entry.amends.filter((amend) => {
        const key = amend?.payload?.amendsKey ?? amend?.payload?.field
        return key === amendsKey
    })
}

function normalizeAmendsForBoundary(amends, amendsKey) {
    return amends.map((amend) => {
        if (amend?.payload?.amendsKey) return amend
        return { ...amend, payload: { ...amend.payload, amendsKey } }
    })
}
